import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { BarChart3, Clapperboard, Eye, Film, ImageIcon, MessageSquare, Pencil, Plus, Share2, ThumbsUp, Trophy, FileText } from "lucide-react";
import { Button, Card, EmptyState, PageHeader } from "../components/ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import type { Work } from "../types";

const MILESTONES = [1, 10, 30, 100];

export function WorksPage() {
  const { dataRevision, mutate } = useApp();
  const [works, setWorks] = useState<Work[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Work | null>(null);
  const [metrics, setMetrics] = useState({ views: "", likes: "", comments: "", shares: "" });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const requestSequence = useRef(0);

  const refresh = useCallback(async () => {
    const sequence = ++requestSequence.current;
    try {
      const result = await api.listWorks();
      if (sequence !== requestSequence.current) return;
      setWorks(result);
      setLoaded(true);
      setLoadError(null);
    } catch (error) {
      if (sequence === requestSequence.current) setLoadError(error instanceof Error ? error.message : "作品读取失败");
    }
  }, []);
  useEffect(() => {
    void refresh();
    return () => { requestSequence.current += 1; };
  }, [dataRevision, refresh]);

  const save = async <T,>(action: () => Promise<T>, message: string) => {
    if (pending.current) return null;
    pending.current = true;
    setSaving(true);
    try { return await mutate(action, message); }
    finally { pending.current = false; setSaving(false); }
  };

  const submitWork = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const result = await save(
      () =>
        api.createWork({
          title: String(form.get("title") ?? ""),
          kind: String(form.get("kind") ?? "video"),
          platform: String(form.get("platform") ?? ""),
          url: String(form.get("url") ?? ""),
          reflection: String(form.get("reflection") ?? ""),
          status: String(form.get("status") ?? "DRAFT")
        }),
      "作品已登记"
    );
    if (result === null) return;
    setAdding(false);
    await refresh();
  };

  const submitMetrics = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    const result = await save(
      () =>
        api.updateWork(editing.id, {
          views: Number(metrics.views || editing.views),
          likes: Number(metrics.likes || editing.likes),
          comments: Number(metrics.comments || editing.comments),
          shares: Number(metrics.shares || editing.shares)
        }),
      "数据已更新"
    );
    if (result === null) return;
    setEditing(null);
    await refresh();
  };

  const totalViews = works.reduce((sum, work) => sum + work.views, 0);
  const totalLikes = works.reduce((sum, work) => sum + work.likes, 0);
  const engagement = totalViews ? Math.round((totalLikes / totalViews) * 1000) / 10 : 0;
  const best = [...works].sort((a, b) => b.views - a.views)[0];

  return (
    <div className="page page--works works-page">
      <PageHeader
        eyebrow="PORTFOLIO · 作品集"
        title="作品集"
        description="每一个作品都是资产。登记它、记录数据、复盘迭代——作品数会变成你的底气。"
        actions={<Button icon={<Plus size={15} />} onClick={() => setAdding(true)}>登记作品</Button>}
      />

      {loadError && <div role="alert"><p>作品集未能刷新：{loadError}。已有内容仍保留。</p><Button variant="secondary" onClick={() => void refresh()}>重新读取</Button></div>}
      {!loaded && !loadError && <p role="status">正在读取作品…</p>}

      <div className="works-stats">
        <div className="works-stat"><strong>{works.length}</strong><span>作品</span></div>
        <div className="works-stat"><strong>{totalViews.toLocaleString()}</strong><span>总播放</span></div>
        <div className="works-stat"><strong>{engagement}%</strong><span>平均互动率</span></div>
        {best && <div className="works-stat works-stat--best"><strong>{best.title.slice(0, 8)}</strong><span>最佳作品 · {best.views.toLocaleString()} 播放</span></div>}
      </div>

      <div className="milestone-row">
        {MILESTONES.map((milestone) => {
          const unlocked = works.length >= milestone;
          return <div key={milestone} className={`milestone ${unlocked ? "milestone--unlocked" : ""}`}><Trophy size={16} /><span>{unlocked ? `已解锁 · 作品 ${works.length}` : `作品 ${milestone}`}</span></div>;
        })}
      </div>

      {adding && (
        <Card className="work-add">
          <h3><Plus size={16} /> 登记新作品</h3>
          <form onSubmit={(event) => void submitWork(event)} className="plan-form">
            <div className="field field--full"><span>作品标题 *</span><input name="title" required placeholder="如：打样流程 · 30秒卡点" /></div>
            <div className="field"><span>类型</span><select name="kind" defaultValue="video"><option value="video">视频</option><option value="image">图文</option><option value="text">纯文案</option></select></div>
            <div className="field"><span>平台</span><select name="platform" defaultValue=""><option value="">未发布</option><option>抖音</option><option>小红书</option><option>视频号</option><option>Instagram</option><option>TikTok</option></select></div>
            <div className="field"><span>发布链接</span><input name="url" placeholder="https://…" /></div>
            <div className="field"><span>状态</span><select name="status" defaultValue="DRAFT"><option value="DRAFT">草稿</option><option value="PUBLISHED">已发布</option></select></div>
            <div className="field field--full"><span>一句话复盘</span><input name="reflection" placeholder="如：钩子放第 3 秒，完播率不错" /></div>
            <div className="modal__actions"><Button disabled={saving} type="button" variant="ghost" onClick={() => setAdding(false)}>取消</Button><Button disabled={saving} type="submit">{saving ? "正在保存…" : "登记"}</Button></div>
          </form>
        </Card>
      )}

      {works.length === 0 && loaded && !loadError ? (
        <EmptyState icon={<Clapperboard size={24} />} title="还没有作品" description="从第一个开始——哪怕只有 30 秒。作品 #1 就是你作品的起点。" actionLabel="登记第一个作品" onAction={() => setAdding(true)} />
      ) : (
        <div className="works-grid">
          {works.map((work, idx) => (
            <Card key={work.id} className="work-card">
              <div className="work-card__badge">作品 #{works.length - idx}</div>
              <h3>{work.title}</h3>
              <div className="work-card__meta"><span>{work.kind === "video" ? <Film size={12} /> : work.kind === "image" ? <ImageIcon size={12} /> : <FileText size={12} />}{work.kind === "video" ? "视频" : work.kind === "image" ? "图文" : "文案"}</span><span>{work.platform || "未发布"}</span><span className={work.status === "PUBLISHED" ? "published" : ""}>{work.status === "PUBLISHED" ? "已发布" : "草稿"}</span></div>
              <div className="work-card__metrics"><span><Eye size={13} /> {work.views.toLocaleString()}</span><span><ThumbsUp size={13} /> {work.likes}</span><span><MessageSquare size={13} /> {work.comments}</span><span><Share2 size={13} /> {work.shares}</span></div>
              {work.reflection && <p className="work-card__reflection">{work.reflection}</p>}
              <Button size="sm" variant="secondary" icon={<Pencil size={13} />} onClick={() => { setEditing(work); setMetrics({ views: String(work.views), likes: String(work.likes), comments: String(work.comments), shares: String(work.shares) }); }}>更新数据</Button>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="modal__header"><div><span className="eyebrow">METRICS</span><h2>更新数据 · {editing.title.slice(0, 24)}</h2><p>播放/点赞/评论/转发——照平台后台填。</p></div><button className="modal__close" onClick={() => setEditing(null)}>✕</button></div>
            <form onSubmit={(event) => void submitMetrics(event)}>
              <div className="modal__body form-grid form-grid--two">
                <label className="field"><span>播放</span><input type="number" value={metrics.views} onChange={(event) => setMetrics({ ...metrics, views: event.target.value })} /></label>
                <label className="field"><span>点赞</span><input type="number" value={metrics.likes} onChange={(event) => setMetrics({ ...metrics, likes: event.target.value })} /></label>
                <label className="field"><span>评论</span><input type="number" value={metrics.comments} onChange={(event) => setMetrics({ ...metrics, comments: event.target.value })} /></label>
                <label className="field"><span>转发</span><input type="number" value={metrics.shares} onChange={(event) => setMetrics({ ...metrics, shares: event.target.value })} /></label>
              </div>
              <div className="modal__footer"><Button disabled={saving} type="button" variant="ghost" onClick={() => setEditing(null)}>取消</Button><Button disabled={saving} type="submit">{saving ? "正在保存…" : "保存数据"}</Button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
